/**
 * Paddle.js — checkout overlay (Billing).
 * Solo token cliente (público); secretos viven en servidor (webhook).
 * @see docs/DOC_PADDLE_CHECKOUT_V1.md
 */

const PADDLE_SCRIPT = 'https://cdn.paddle.com/paddle/v2/paddle.js';

let paddleLoadPromise = null;

function getPaddleEnv() {
  const raw = (import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox').toString().toLowerCase();
  return raw === 'production' ? 'production' : 'sandbox';
}

/**
 * Carga el script Paddle una sola vez.
 * @returns {Promise<typeof window.Paddle>}
 */
export function loadPaddleJs() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('PADDLE_NO_WINDOW'));
  }
  if (window.Paddle) {
    return Promise.resolve(window.Paddle);
  }
  if (paddleLoadPromise) return paddleLoadPromise;
  paddleLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${PADDLE_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Paddle));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = PADDLE_SCRIPT;
    s.async = true;
    s.onload = () => resolve(window.Paddle);
    s.onerror = () => reject(new Error('PADDLE_SCRIPT_LOAD_FAILED'));
    document.head.appendChild(s);
  });
  return paddleLoadPromise;
}

/**
 * @returns {boolean}
 */
export function isPaddleCheckoutConfigured() {
  const token = (import.meta.env.VITE_PADDLE_CLIENT_TOKEN || '').trim();
  const priF = (import.meta.env.VITE_PADDLE_PRICE_ID_FOUNDATION || '').trim();
  const priP = (import.meta.env.VITE_PADDLE_PRICE_ID_PRO || '').trim();
  return Boolean(token && priF && priP);
}

/**
 * Paddle exige custom_data con valores serializables; normalizamos a string.
 * @param {Record<string, unknown>} obj
 * @returns {Record<string, string>}
 */
function toPaddleCustomData(obj) {
  const out = {};
  if (!obj || typeof obj !== 'object') {
    return { archat: '1' };
  }
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v === null || v === undefined) return;
    out[String(k).slice(0, 64)] = String(v).slice(0, 500);
  });
  if (Object.keys(out).length === 0) {
    out.archat = '1';
  }
  return out;
}

/**
 * Resuelve priceId según plan interno foundation | pro
 * @param {string} selectedPlan
 * @returns {string|null}
 */
export function resolvePaddlePriceId(selectedPlan) {
  const p = String(selectedPlan || '').toLowerCase();
  if (p === 'foundation') {
    return (import.meta.env.VITE_PADDLE_PRICE_ID_FOUNDATION || '').trim() || null;
  }
  if (p === 'pro') {
    return (import.meta.env.VITE_PADDLE_PRICE_ID_PRO || '').trim() || null;
  }
  return null;
}

/**
 * Abre Paddle Checkout (overlay). No activa plan en cliente.
 *
 * @param {object} opts
 * @param {string} opts.priceId - pri_…
 * @param {Record<string, unknown>} opts.customData
 * @param {string|null} opts.customerEmail
 * @param {string} opts.successUrl - absoluta https
 * @param {string} opts.cancelUrl - absoluta https
 */
export async function openPaddleOverlayCheckout({
  priceId,
  customData,
  customerEmail,
  successUrl,
  cancelUrl,
}) {
  const token = (import.meta.env.VITE_PADDLE_CLIENT_TOKEN || '').trim();
  if (!token) {
    throw new Error('PADDLE_CLIENT_TOKEN_MISSING');
  }
  if (!priceId) {
    throw new Error('PADDLE_PRICE_ID_MISSING');
  }
  const Paddle = await loadPaddleJs();
  if (!Paddle || typeof Paddle.Initialize !== 'function') {
    throw new Error('PADDLE_SDK_INVALID');
  }

  const environment = getPaddleEnv();
  if (Paddle.Environment && typeof Paddle.Environment.set === 'function') {
    Paddle.Environment.set(environment);
  }
  const initResult = Paddle.Initialize({
    token,
  });
  if (initResult && typeof initResult.then === 'function') {
    await initResult;
  }

  const cd = toPaddleCustomData(customData);

  const payload = {
    items: [{ priceId, quantity: 1 }],
    customData: cd,
    settings: {
      displayMode: 'overlay',
      theme: 'dark',
      successUrl,
      // cancelUrl — si la SDK no lo aplica en overlay, el usuario cierra el modal; documentado
    },
  };

  if (customerEmail && String(customerEmail).includes('@')) {
    payload.customer = { email: String(customerEmail).trim() };
  }

  if (typeof Paddle.Checkout?.open === 'function') {
    Paddle.Checkout.open(payload);
    return;
  }
  throw new Error('PADDLE_CHECKOUT_OPEN_UNAVAILABLE');
}
